import Navigator from "../../models/navigator";
import ReactDOM from "react-dom/client";
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import DataTable from "datatables.net-dt";
import "datatables.net-columncontrol-dt";
import "datatables.net-select-dt";
import type { Api } from "datatables.net";
import "datatables.net-scroller";
import tableService from "../../services/tableService";
import ProjectInfo from "../../models/projectInfo";
import TableColumnDataType from "../../models/tableColumnDataType";
import Settings from "../../models/settings";
import TableManager from "../../models/tableManager";
import Navigable, { NavigatorMove, SetFilters } from "../../models/navigable";
import NavigatorPosition from "../../models/navigatorPosition";
import promiseHelper from "../../util/promiseHelper";
import LoadingIndicatorLargeCenter from "../common/LoadingIndicatorLargeCenter";
import Filter from "../../models/filter";
import { TableRef } from "./Table";
import tableCommon from "./tableCommon";
import filterHelper from "../../models/filterHelper";
import unitConverter from "../../util/units/unitConverter";
import Unit from "../../util/units/unit";

interface Props {
  navigator: Navigator;
  project: ProjectInfo;
  settings: Settings;
  //children: React.ReactNode;
}

const TableFullData = forwardRef((props: Props, ref: Ref<TableRef>) => {
  const { navigator, project, settings } = props;

  const gridRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({ refresh }));

  const [tableManager] = useState<TableManager>(new TableManager());

  const [showHelperColumns, setShowHelperColumns] = useState<boolean>(
    settings.showTableHelperColumns,
  );

  const [filters, setFilters] = useState<Filter[]>([]);

  const [initSelectedIndex, setInitSelectedIndex] = useState<number>(-1);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const refresh = () => {
    setTimeout(() => {
      setSize();
    }, 100);
  };

  const setSize = useCallback(() => {
    const height = gridRef.current?.offsetHeight ?? 0;
    //const width = gridRef.current?.offsetWidth ?? 0;

    tableCommon.updateScrollY(height, gridRef);
  }, []);

  useEffect(() => {
    const tableNode = tableRef.current;

    if (!tableNode) {
      return;
    }

    const navigable = new Navigable();
    navigable.id = "table";

    let table: Api<any> | undefined;
    let isDisposed = false;

    let selectedIndex: number = initSelectedIndex;

    const init = async () => {
      if (!tableNode) {
        return;
      }

      setIsBusy(true);

      // row count

      const { data: tableRowsCount } = await tableService.getTableRowsCount(
        project.idProject,
        filters,
      );

      console.log("table-rowCount", tableRowsCount);

      totalRowsCount = tableRowsCount;
      filteredRowsCount = tableRowsCount;

      tableManager.init(tableRowsCount);

      if (isDisposed) {
        return;
      }

      // columns

      const { data: tableColumns } = await tableService.getTableColumns(
        project.idProject,
      );

      console.log("table-columnCount", tableColumns.length);

      if (isDisposed) {
        return;
      }

      if (showHelperColumns) {
        const helperColumnNames = tableManager.getHelperColumnNames();

        for (const helperColumnName of helperColumnNames) {
          tableColumns.push({
            columnName: helperColumnName,
            displayName: helperColumnName,
            dataType: TableColumnDataType.Number,
            unitMeasure: "",
            decimals: 3,
            index: -1,
          });
        }
      }

      const columns = tableCommon.getConfigColumns(tableColumns, project);

      // table data

      const startIndex = 0;
      const stopIndex = tableRowsCount - 1;

      const { data: tableDataResponse } = await tableService.getDataTableData(
        1,
        startIndex,
        tableRowsCount,
        project.idProject,
        filters,
      );

      const tableData = tableDataResponse.data;

      if (isDisposed) {
        return;
      }

      tableManager.addItems(tableData, startIndex, stopIndex);

      table = new DataTable(tableNode, {
        deferRender: true,
        paging: true,
        scroller: true,
        scrollCollapse: true,
        scrollX: true,
        scrollY: "200px",
        searching: true,
        dom: "t",
        columns: columns,
        order: [],
        ordering: {
          indicators: false,
          handler: false,
        },
        select: { style: "single", toggleable: false } as any,

        // full data
        data: tableData,

        // sorting and filering

        columnControl: ["order", ["orderClear", "search", "searchClear"]],

        language: {
          columnControl: {
            searchClear: "Clear filter",
          },
        } as any,
      });

      table.on("order.dt", function (e, settings, order) {
        // console.log("sorting changed:", order);
        setTimeout(() => {
          scrollToAndSelectIndex(table, selectedIndex);
        }, 50);
      });

      table.on("search.dt", function (e, settings) {
        // console.log("filter changed:", settings.oPreviousSearch.sSearch);
        setTimeout(() => {
          scrollToAndSelectIndex(table, selectedIndex);

          if (!table) {
            return;
          }

          // filtering

          tableFilters = [];

          const state = settings.oSavedState as {
            columnControl: Record<
              string,
              { searchInput: { logic: string; type: string; value: string } }
            >;
          };

          if (state?.columnControl) {
            for (const [colIndex, control] of Object.entries(
              state.columnControl,
            )) {
              const value = control.searchInput.value;
              if (value.trim() !== "") {
                //console.log(JSON.stringify(control.searchInput));

                const colIdx = parseInt(colIndex, 10);
                // const header = table?.column(colIdx).header() as HTMLElement;
                // const title = header?.textContent?.trim() || `Column ${colIdx}`;
                const column = table?.column(colIdx);
                if (column) {
                  const colDef = table?.settings()[0].aoColumns[column.index()];

                  const filter: Filter = new Filter();
                  filter.fieldDisplayName = colDef.title;
                  filter.fieldName = colDef.data;
                  filter.fieldDisplayValue = value.trim();

                  const tableColumn = tableColumns.find(
                    (c) => c.columnName === colDef.data,
                  );

                  if (tableColumn && tableColumn.unitMeasure !== Unit.None) {
                    filter.fieldUnit = tableColumn.unitMeasure;
                    filter.fieldValue = unitConverter
                      .convertValueToBaseUnit(
                        Number(filter.fieldDisplayValue),
                        tableColumn.unitMeasure,
                      )
                      .toString();
                  } else {
                    filter.fieldValue = value.trim();
                  }

                  filter.fieldType = columnControlDataTypeToTableColumnDataType(
                    control.searchInput.type,
                  );
                  filter.fieldLogic = control.searchInput.logic;

                  tableFilters.push(filter);
                }
              }
            }

            navigator.setTableFilters(tableFilters);

            updateFiltersTextDisplay();

            // filtered records

            //totalRowsCount = (table as any).rows().count();

            filteredRowsCount = (table as any)
              .rows({ filter: "applied" })
              .count();

            updateFilteredRowsDisplay();
          }
        }, 50);
      });

      const columnControlDataTypeToTableColumnDataType = (
        columnControlDataType: string,
      ) => {
        if (columnControlDataType === "text") {
          return TableColumnDataType.String;
        } else if (columnControlDataType === "num") {
          return TableColumnDataType.Number;
        }

        return TableColumnDataType.String;
      };

      table.on(
        "user-select",
        async function (e, dt, type, cell, originalEvent) {
          if (type === "row") {
            let index = cell.index().row;

            // looks that the index is returned in orginal data index not visual index (sorted and filtered)

            // console.log("user clicked row:", table.row(cell.index().row).data());
            // console.log("user-select-index", index);

            selectedIndex = index;

            const rowData = tableManager.getItems(index);
            await tableCommon.onRowSelected(
              rowData,
              tableManager,
              navigator,
              navigable,
              project,
            );
          }
        },
      );

      setSize();

      setIsBusy(false);
    };

    const scrollToAndSelectIndex = (table: any, index: number) => {
      // for sorting and filtering
      const visualIndex = getVisualIndex(table, index);
      // console.log(
      //   `scrollToAndSelectIndex - visualIndex: ${visualIndex}, dataIndex: ${index}`
      // );
      scrollTo(table, visualIndex);
      selectIndex(table, index);
    };

    const scrollTo = (table: any, index: number) => {
      const scrollToIndex = index - 3;
      // console.log("scrollToIndex", scrollToIndex);
      table.scroller.toPosition(scrollToIndex, false);
    };

    const selectIndex = (table: any, index: number) => {
      // console.log("selectIndex");

      if (index === -1) {
        table.rows().deselect();
        return;
      }

      table.row(index).select();
      // console.log("selectIndex-indexSelected", index);
    };

    settings.onShowTableHelperColumnsChanged = (checked: boolean) => {
      setShowHelperColumns(settings.showTableHelperColumns);
      setInitSelectedIndex(selectedIndex);
    };

    // get the visual index (position on screen) from a data index
    // if sorting and filtering is applied the visual index will differ from dataIndex
    function getVisualIndex(table: any, dataIndex: number) {
      const orderedIndexes = table
        .rows({ order: "applied", search: "applied" })
        .indexes()
        .toArray();
      return orderedIndexes.indexOf(dataIndex); // -1 if not currently displayed
    }

    // get the data index (original) from a visual index (position on screen)
    // function getDataIndex(table: any, visualIndex: any) {
    //   const orderedIndexes = table
    //     .rows({ order: "applied", search: "applied" })
    //     .indexes()
    //     .toArray();
    //   return orderedIndexes[visualIndex]; // undefined if out of range
    // }

    const navigatorMove: NavigatorMove = async (
      navigatorPositon: NavigatorPosition,
    ) => {
      if (isDisposed) {
        return;
      }

      while (!tableManager.hasItems() || !table) {
        await promiseHelper.delay(100);
      }

      let itemIndex = tableManager.getItemIndex(
        navigatorPositon.idRoad,
        navigatorPositon.idSession,
        navigatorPositon.distance,
      );

      if (itemIndex !== -1) {
        // console.log("table: index found", itemIndex);
        selectedIndex = itemIndex;
        scrollToAndSelectIndex(table, itemIndex);
      } else {
        // console.log("table: index not found");
        selectedIndex = -1;
        selectIndex(table, -1);
      }
    };
    navigable.navigatorMove = navigatorMove;

    const onSetFilters: SetFilters = async (
      filters: Filter[],
      isProjectChanging: boolean,
    ) => {
      setFilters(filters);
    };
    navigable.setFilters = onSetFilters;

    navigator.attach(navigable);

    init();

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      // console.log(`tableFullData:navigator:move()-show-hide-init`);
      navigatorMove(navigator.navigatorPosition);
    }

    let lastColumnNameClicked: string | undefined = "";

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          setTimeout(() => {
            if (
              node instanceof HTMLElement &&
              node.classList.contains("dtcc-button_dropdown")
            ) {
              node.onclick = (e) => {
                const colHeader = node.closest(".dt-column-header");
                const title = colHeader?.querySelector(".dt-column-title");
                lastColumnNameClicked = title?.innerHTML;
              };
            }

            if (
              node instanceof HTMLElement &&
              node.classList.contains("dtcc-dropdown")
            ) {
              createColumnNameContainer(node);
            }
          }, 0);

          if (
            node instanceof HTMLElement &&
            node.classList.contains("dtcc-dropdown")
          ) {
            // focus on search input

            const searchInput = node.querySelector("input");

            if (searchInput) {
              searchInput.focus();
            }

            createClearAllFiltersContainer(node);

            createFiltersTextContainer(node);

            createFilteredRowsContainer(node);
          }
        });
      });
    });

    const createColumnNameContainer = (parentContainerNode: HTMLElement) => {
      const cssClass = "column-name";

      const existingContainer = parentContainerNode.querySelector(
        `.${cssClass}`,
      );

      if (existingContainer) {
        existingContainer.parentElement?.removeChild(existingContainer);
      }

      const columnNameContainer = document.createElement("div");
      columnNameContainer.className = `${cssClass} dtcc-dropdown-liner`;

      if (lastColumnNameClicked) {
        columnNameContainer.style.padding = "6px 12px";
        columnNameContainer.style.fontWeight = "bold";
        columnNameContainer.innerHTML = lastColumnNameClicked ?? "";
      }

      parentContainerNode.insertBefore(
        columnNameContainer,
        parentContainerNode.firstChild,
      );
    };

    const createClearAllFiltersContainer = (
      parentContainerNode: HTMLElement,
    ) => {
      const cssClass = "clear-all-btn";

      const existingContainer = parentContainerNode.querySelector(
        `.${cssClass}`,
      );

      if (existingContainer) {
        existingContainer.parentElement?.removeChild(existingContainer);
      }

      const clearAllContainer = document.createElement("div");
      clearAllContainer.className = `${cssClass} dtcc-dropdown-liner`;
      parentContainerNode.appendChild(clearAllContainer);

      const clearAllRoot = ReactDOM.createRoot(clearAllContainer);
      clearAllRoot.render(
        <button
          className="dtcc-button"
          onClick={() => {
            if (!table) {
              return;
            }

            table.columns().every(function (this: any) {
              this.ccSearchClear();
              return true;
            });

            // table
            //   .columns()
            //   .indexes()
            //   .toArray()
            //   .forEach((i: number) => {
            //     if (table) {
            //       (table.column(i) as any).ccSearchClear();
            //     }
            //   });

            table.search("").draw();
          }}
        >
          <span className="dtcc-button-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                marginTop: "3px",
                marginLeft: "2px",
              }}
            >
              <path
                fill="currentColor"
                d="M2 2l12 12m0-12L2 14"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </span>
          <span className="dtcc-button-text">Clear all filters</span>
          <span className="dtcc-button-state"></span>
          <span className="dtcc-button-extra"></span>
        </button>,
      );
    };

    let totalRowsCount = -1;
    let filteredRowsCount = -1;
    let filteredRowsContainer: HTMLDivElement | undefined;

    const createFilteredRowsContainer = (parentContainerNode: HTMLElement) => {
      const cssClass = "filtered-rows";

      const existingContainer = parentContainerNode.querySelector(
        `.${cssClass}`,
      );

      if (existingContainer) {
        existingContainer.parentElement?.removeChild(existingContainer);
      }

      filteredRowsContainer = document.createElement("div");
      filteredRowsContainer.className = `${cssClass} dtcc-dropdown-liner`;

      filteredRowsContainer.style.padding = "6px 12px";
      //filteredRowsContainer.style.fontWeight = "bold";
      updateFilteredRowsDisplay();

      parentContainerNode.appendChild(filteredRowsContainer);
    };

    const updateFilteredRowsDisplay = () => {
      let displayMessage = `Filtered records ${filteredRowsCount} / ${totalRowsCount}`;

      if (totalRowsCount === filteredRowsCount) {
        // "No filters";
        displayMessage = `Total records ${totalRowsCount}`;
      }

      if (filteredRowsContainer) {
        filteredRowsContainer.innerHTML = displayMessage;
      }
    };

    let tableFilters: Filter[] = [];
    let filtersTextContainer: HTMLDivElement | undefined;

    const createFiltersTextContainer = (parentContainerNode: HTMLElement) => {
      const cssClass = "filters-text";

      const existingContainer = parentContainerNode.querySelector(
        `.${cssClass}`,
      );

      if (existingContainer) {
        existingContainer.parentElement?.removeChild(existingContainer);
      }

      filtersTextContainer = document.createElement("div");
      filtersTextContainer.className = `${cssClass} dtcc-dropdown-liner`;

      //filtersTextContainer.style.padding = "6px 12px";

      updateFiltersTextDisplay();

      parentContainerNode.appendChild(filtersTextContainer);
    };

    const updateFiltersTextDisplay = () => {
      let displayMessage = "";

      if (tableFilters.length > 0) {
        const filterString = filterHelper.filtersToText(tableFilters);
        displayMessage = `Filtered by ${filterString}`;
      }

      if (filtersTextContainer) {
        filtersTextContainer.innerHTML = displayMessage;

        if (tableFilters.length > 0) {
          filtersTextContainer.style.padding = "6px 12px";
        } else {
          filtersTextContainer.style.padding = "0px";
        }
      }
    };

    if (gridRef.current) {
      observer.observe(gridRef.current, { childList: true, subtree: true });
    }

    return () => {
      navigator.detach(navigable);
      settings.onShowTableHelperColumnsChanged = undefined;
      tableManager.clearItems();
      isDisposed = true;
      observer.disconnect();
      table?.destroy();
      // clear table content
      tableNode.innerHTML = "";
    };
  }, [
    project,
    navigator,
    tableManager,
    setSize,
    settings,
    showHelperColumns,
    initSelectedIndex,
    filters,
  ]);

  return (
    <div
      ref={gridRef}
      style={{ height: "100%", width: "100%", fontSize: "12px", border: "0px" }}
    >
      {isBusy && <LoadingIndicatorLargeCenter />}
      <table className="display compact" ref={tableRef}></table>
    </div>
  );
});

export default TableFullData;
